import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from 'react';

export interface Toast {
  id: number;
  type: 'info' | 'success' | 'warning' | 'error' | 'confirmation';
  message: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
  };
  confirmAction?: {
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
  };
  duration?: number | null; // Auto-dismiss time in ms, null for manual dismiss
  leaving?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: Toast['type'], options?: {
    action?: Toast['action'];
    duration?: number;
  }) => void;
  showConfirmationToast: (message: string, options: {
    onConfirm: () => void;
    onCancel?: () => void;
    confirmText?: string;
    cancelText?: string;
    replaceKey?: string;
  }) => void;
  dismissToast: (id: number) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdCounter = useRef(0);
  const timeouts = useRef<Map<number, NodeJS.Timeout>>(new Map());

  const dismissToast = useCallback((id: number) => {
    // Start leaving animation
    setToasts(prev => prev.map(toast => 
      toast.id === id ? { ...toast, leaving: true } : toast
    ));
    
    // Remove after animation
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
      const timeout = timeouts.current.get(id);
      if (timeout) {
        clearTimeout(timeout);
        timeouts.current.delete(id);
      }
    }, 400); // Animation duration
  }, []);

  const showToast = useCallback((
    message: string, 
    type: Toast['type'] = 'info',
    options?: {
      action?: Toast['action'];
      duration?: number;
    }
  ) => {
    const id = ++toastIdCounter.current;
    const duration = options?.duration ?? (type === 'confirmation' ? null : 4000);
    
    const newToast: Toast = {
      id,
      type,
      message,
      action: options?.action,
      duration,
    };

    setToasts(prev => [...prev, newToast]);

    // Auto-dismiss if duration is set
    if (duration) {
      const timeout = setTimeout(() => dismissToast(id), duration);
      timeouts.current.set(id, timeout);
    }

    return id;
  }, [dismissToast]);

  const showConfirmationToast = useCallback((
    message: string,
    options: {
      onConfirm: () => void;
      onCancel?: () => void;
      confirmText?: string;
      cancelText?: string;
      replaceKey?: string;
    }
  ) => {
    const id = ++toastIdCounter.current;
    
    const newToast: Toast = {
      id,
      type: 'confirmation',
      message,
      confirmAction: {
        onConfirm: () => {
          options.onConfirm();
          dismissToast(id);
        },
        onCancel: () => {
          options.onCancel?.();
          dismissToast(id);
        },
        confirmText: options.confirmText || 'Ja',
        cancelText: options.cancelText || 'Avbryt',
      },
      duration: null, // Manual dismiss only
    };

    let existingId: number | null = null;
    setToasts(prev => {
      // For flows like "Stryka ...", keep only the latest relevant confirmation visible.
      const withoutReplaced = options.replaceKey
        ? prev.filter(toast => {
            if (toast.type !== 'confirmation' || toast.leaving) return true;
            const key = toast.message.startsWith('Stryka ') ? 'strike-confirmation' : undefined;
            return key !== options.replaceKey;
          })
        : prev;

      const existing = prev.find(toast => (
        toast.type === 'confirmation' &&
        toast.message === message &&
        !toast.leaving
      ));
      if (existing) {
        existingId = existing.id;
        return withoutReplaced;
      }
      return [...withoutReplaced, newToast];
    });
    return existingId ?? id;
  }, [dismissToast]);

  const clearAllToasts = useCallback(() => {
    // Clear all timeouts
    timeouts.current.forEach(timeout => clearTimeout(timeout));
    timeouts.current.clear();
    
    setToasts([]);
  }, []);

  React.useEffect(() => {
    // Cleanup on unmount
    const currentTimeouts = timeouts.current; // Capture reference
    return () => {
      currentTimeouts.forEach(timeout => clearTimeout(timeout));
      currentTimeouts.clear();
    };
  }, []);

  const value: ToastContextType = {
    toasts,
    showToast,
    showConfirmationToast,
    dismissToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};
